// Module Import
//---------------------------------
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { graphql, navigate, useStaticQuery } from 'gatsby'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'
import '../../../assets/css/typography.scss'
import axios from 'axios'
import { format } from 'date-fns'
import TypographyAtom from '../../atoms/typography/typography'
import ArrowIconOrange from '../../../assets/images/icon_arrowOrange.png'
import ButtonAtom from '../../atoms/buttons/buttons'
import ProductGallery from '../GalleryCarousel/ProductGallery'
// ADD IF PRODUCT FEATURE IMAGE IS REQUIRED
import useStore from '../../../context/StoreContext'
import { useToast } from '../../../context/ToastContext'
import { ROUTES } from '../../../constants/routes'
import RentalForm from '../Forms/rentalForm'
import FileUploadForm from '../Forms/FileUploadForm.jsx'
import { extractDataFromFormData } from '../../../helpers/extractDataFromForm'
import { accessoriesClient } from '../../../utils/product/accessories'
import RentalProductAccessories from '../RentalProductAccessories/RentalProductAccessories'
import EnquireModal from '../Modals/EnquireModal'
import SelectField from '../../atoms/fields/select'
import RentalPoductModal from '../Modals/RentalProductModal/RentalProductModal'
import AddReviewButton from '../AddReviewButton/AddReviewButton'
import PriceTag from '../PriceTag/PriceTag'
import ReviewsModal from '../Modals/ReviewsModal/ReviewsModal'
import { getReviewsByProduct } from '../../../utils/review/review'
import getExternalProductId from '../../../helpers/getExternalProductId'
import StockLabel from '../StockLabel/StockLabel'
import QuantityField from '../../atoms/fields/quantity'
import validateCaptchaToken from '../../../utils/captcha/validateCaptchaToken'
import SaleLabel from '../SaleLabel/SaleLabel'
import useFormValidation from '../../../hooks/useFormValidation'
import { rentalSchema } from '../../../utils/form/validationSchema'
import LinkAtom from '../../atoms/links/links'
import getUpgradeButtonLink from '../../../helpers/getUpgradeButtonLink'
import {
  brandDarkGrey,
  brandIvory,
  brandOrange,
  brandSoftOrange,
} from '../../../assets/consts/color.js'
import { primaryFont, secondaryFont } from '../../../assets/consts/fonts.js'
import productApi from '../../../utils/product/productApi.js'
import RelatedProducts from '../RelatedProducts/RelatedProducts.jsx'
import Loader from '../../atoms/loader/Loader.jsx'
import getDataLayerProductInfo from '../../../utils/dataLayer/getDataLayerProductInfo.js'
import useCustomer from '../../../context/CustomerContext.jsx'
import NotifyProduct from '../NotifyProduct/NotifyProduct.jsx'
import nodeApi from '../../../utils/api/nodeApi/nodeApi.js'

// Component Import
//---------------------------------

// Styled Component Declarations
//---------------------------------
const ProductHeaderWrapper = styled.div`
  h1 {
    margin: 0;
  }

  button {
    &:focus {
      &:hover {
        background-color: ${brandSoftOrange};
        color: ${brandOrange};
        cursor: pointer;

        &:after {
          background-image: url(${ArrowIconOrange});
          transform: rotate(90deg);
        }
      }
    }
  }
`

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 0 64px 0;
  flex-wrap: wrap;

  .purchasePrice {
    width: fit-content;
    background: ${brandIvory};
    padding: 16px;

    p {
      text-wrap: nowrap;
      font-family: ${primaryFont};
      color: ${brandDarkGrey};
    }
  }

  a {
    text-decoration: none;
  }
`

const PriceWrapperFooter = styled.div`
  align-items: center;
  display: flex;
  ${(props) => props.addSpace && 'justify-content: space-between;'}
  flex-wrap: wrap;
  gap: 20px 8px;

  .priceWrapper.rental {
    display: flex;
    align-items: flex-end;
    gap: 8px;
  }

  @media only screen and (min-width: 1024px) and (max-width: 1750px) {
    & > div > div:first-child:not(.purchasePrice) {
      width: 170px;
    }
  }

  @media only screen and (max-width: 850px) {
    gap: 20px;

    & > div > div:first-child:not(.purchasePrice) {
      width: 170px;
    }
  }

  @media only screen and (min-width: 48em) {
    .priceWrapper:not(.rental) {
      p:last-child {
        font-size: 18px;
      }
    }

    p {
      font-size: 32px;
    }
  }

  .priceWrapper:not(.rental) {
    p:last-child {
      font-size: 18px;
    }
  }

  p {
    font-size: 16px;
    margin: 0;
  }
`

const PriceFooterInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;

  @media only screen and (max-width: 850px) and (min-width: 480px) {
    gap: 16px;
  }

  @media only screen and (max-width: 480px) {
    width: 100%;

    .stock-label,
    .add-review-button {
      width: 100% !important;
    }
  }

  @media only screen and (min-width: 48em) {
    gap: 16px;
  }
`

const ImageWrapper = styled.div`
  position: relative;

  @media only screen and (min-width: 48em) {
    position: sticky;
    top: 120px;
  }
`

const PurchasePriceWrapper = styled.div``

const StickyWrapper = styled.div`
  @media only screen and (min-width: 48em) {
    position: sticky;
    top: 120px;
  }
`

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ul {
    list-style: none;
    padding: 0;

    li {
      b {
        display: block;
        color: ${brandOrange};
      }
    }
  }
`

const DisabledButtonWrapper = styled.div`
  touch-action: none;
  user-select: none;
  pointer-events: none;
  opacity: 0.5;
`

const AddToCartWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-shrink: 0;
  flex-grow: 1;
  gap: 10px;

  @media only screen and (max-width: 850px) and (min-width: 480px) {
    gap: 20px;
  }

  @media only screen and (max-width: 480px) {
    justify-content: space-between;
    width: 100%;
  }

  @media only screen and (min-width: 48em) {
    gap: 40px;
  }

  @media only screen and (max-width: 490px) {
    flex-wrap: wrap;
  }

  @media only screen and (min-width: 1751px) and (max-width: 1990px) {
    gap: 80px;
  }

  @media only screen and (min-width: 851px) and (max-width: 935px) {
    gap: 80px;
  }
`

const SkuText = styled.p`
  font-family: ${secondaryFont};
  color: ${brandOrange};
  font-size: 20px;
  margin: 0;
`

const ButtonsWrapper = styled.div`
  height: 100%;
  display: flex;
  gap: 4px;
`

const defaultValues = {
  startDate: '',
  finishDate: '',
  weeksCount: 1,
}

const productsByType = {
  LARGE_DRONES: ['m350', 'inspire 3', 'm30'],
  SMALL_DRONES: ['mavic', 'mini', 'air'],
}

// Component Export
//---------------------------------
export default function ProductHeader({
  productTitle,
  productType,
  productId,
  productDescriptionShopify,
  productDescriptionContentful,
  totalCount,
  variants = [],
  isInventoryTracks,
  tags,
  productImages,
  rentalMetaFields,
  relateProductsIdsArray = [],
  familyCollection,
  shopifyId,
  upgradeButtonText,
  upgradeButtonLink,
  changeVariant,
  variant,
  location,
  breadCrumbs,
  isFreeCourseProduct,
  freeCourseRef,
  isCtaTextDisplayed,
  ctaTextBox,
  bonusItems,
}) {
  const reviewsModalRef = useRef(null)
  const [bookings, setBookings] = useState([])
  const [accessories, setAccessories] = useState([])
  const [relatedProducts, setRelatedProducts] = useState([])
  const [isRelatedProductsLoading, setIsRelatedProductsLoading] = useState(false)
  const [bonusProducts, setBonusProducts] = useState([])
  const [isBonusProductsLoading, setIsBonusProductsLoading] = useState(false)
  const [bookingsAccessories, setBookingsAccessories] = useState({
    loading: true,
    setup: false,
    data: {},
  })
  const [formValues, setFormValues] = useState(defaultValues)
  const [isLoading, setLoading] = useState(false)
  const [showNotify, setShowNotify] = useState(false)
  const [selectedVariant, setSelectedVariant] = useState(variant ?? {})
  const [reviews, setReviews] = useState([])
  const [isRentalModalOpen, setIsRentalModalOpen] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [isReviewsOpen, setIsReviewsOpen] = useState(false)
  const [reviewsPage, setReviewsPage] = useState(1)
  const [pageAdditionalValue, setPageAdditionalValue] = useState(0)
  const [isLoadMore, setIsLoadMore] = useState(true)
  const [ratingInfo, setRatingInfo] = useState({ totalReviews: 0, productRating: 0 })
  const [isReviewsLoading, setIsReviewsLoading] = useState(false)
  const [quantity, setQuantity] = useState(1)
  const [actualTotalCount, setActualTotalCount] = useState('pending')
  const [availableForSale, setAvailableForSale] = useState(totalCount > 0)

  const { customer } = useCustomer()
  const { addVariantToCart, addVariantsListToCart } = useStore()
  const { showToast } = useToast()
  const { validateValues, validateFieldOnChange, errors } = useFormValidation({
    schema: rentalSchema,
  })

  const featureImageResponse = useStaticQuery(graphql`
    query {
      contentfulComponentConfigData {
        productFeatureImage {
          description
          gatsbyImageData
        }
      }
    }
  `)

  const featureImage = featureImageResponse?.contentfulComponentConfigData?.productFeatureImage

  const isFreeCourseApplied = customer?.tags?.includes('no_delete_L1_course_submitted')

  const urlSearchParams = new URLSearchParams(location.search)
  const isReviewNotification = Boolean(urlSearchParams.get('judgeme_token'))
  const retingFromNotification = urlSearchParams.get('judgeme_rating')

  const shouldVariantSelectShow =
    variants.length > 1 || (variants.length === 1 && variants[0]?.title !== 'Default Title')

  const externalIdForReviews = getExternalProductId(shopifyId)

  const handleCloseRentalModal = () => setIsRentalModalOpen(false)

  const handleSetReviewsPage = () => setReviewsPage(pageAdditionalValue)

  const handleFinishUploading = () => {
    navigate(`${familyCollection ? `${ROUTES.DRONES}/${familyCollection}` : ROUTES.DRONES}`, {
      replace: true,
    })
    setIsRentalModalOpen(false)
  }

  const handleBookNowClick = () => {
    if (!formValues.startDate) {
      showToast('Validation error', 'Please select your rental period')

      return
    }

    setIsRentalModalOpen(true)
  }

  const handleOpenRevies = () => setIsReviewsOpen(true)

  const handleCloseReviews = () => setIsReviewsOpen(false)

  useEffect(() => {
    let show = false

    if (
      tags?.includes('notify') ||
      (actualTotalCount < 1 && isInventoryTracks) ||
      !availableForSale
    ) {
      show = true
    }

    setShowNotify(show)
  }, [actualTotalCount, tags, selectedVariant, isInventoryTracks])

  const isDrone = tags?.some((tag) => ['drone', 'drones'].includes(tag.toLowerCase())) || false

  const hidePrice = tags?.some((tag) => ['enquire only'].includes(tag.toLowerCase())) || false
  const enquireWithPrice =
    tags?.some((tag) => ['enquire with price'].includes(tag.toLowerCase())) || false

  const [isOpen, setIsOpen] = useState(false)

  const handleChangeQuantity = useCallback((value) => {
    setQuantity(value)
  }, [])

  const returnEnquireButtonText = () => {
    if (isFreeCourseProduct) {
      if (isFreeCourseApplied) return 'Go to course'

      return 'Access L1 Course'
    }

    return 'Have a question?'
  }

  const handleOpen = useCallback(() => {
    if (isFreeCourseApplied && isFreeCourseProduct && freeCourseRef) {
      freeCourseRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })

      return
    }

    setIsOpen(true)
  }, [isFreeCourseApplied, isFreeCourseProduct, freeCourseRef])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const getDeliveryPrice = () => {
    if (selectedVariant?.weight !== undefined && selectedVariant?.weightUnit === 'KILOGRAMS') {
      if (selectedVariant.weight > 10) {
        return 40
      }
      return 20
    }

    const type = productsByType.LARGE_DRONES.some((drone) =>
      productTitle.toLowerCase().includes(drone)
    )
      ? 'large'
      : 'small'
    if (type === 'large') {
      return 40
    }

    return 20
  }

  const deliveryPrice = getDeliveryPrice()

  const cartProductData = useMemo(() => {
    let firstImage = ''
    if (productImages) {
      firstImage = productImages[0]?.preview?.image?.originalSrc
    } else {
      firstImage = ''
    }

    return {
      id: productId,
      title: productTitle,
      productType,
      price: selectedVariant?.price,
      image: firstImage,
      variants: [{ shopifyId: selectedVariant?.shopifyId }],
    }
  }, [productTitle, productId, productImages, selectedVariant])

  const rentalLink = rentalMetaFields.find((metafield) => metafield.key === 'link')?.value || ''
  const rentalLinkSplit = rentalLink.split('/')
  const rentalProductSlug = rentalLinkSplit[rentalLinkSplit.length - 1]

  const rentalConfig = rentalMetaFields.find((metafield) => metafield.key === 'json_config')

  const rrp = rentalMetaFields.find((metafield) => metafield.key === 'rrp')?.value

  const accessoriesMetafield = rentalMetaFields.find(
    (metafield) => metafield.key === 'accessories'
  )?.value

  const sku = selectedVariant?.sku

  const isSale =
    selectedVariant?.compareAtPrice > 0 &&
    selectedVariant?.compareAtPrice - selectedVariant?.price > 0

  const onAddRelatedProducts = useCallback(
    (productData) => {
      const product = {
        id: productData?.id,
        productType: productData.productType,
        title: productData?.title,
        image: productData?.featuredImage?.url,
        price: productData?.priceRange?.maxVariantPrice?.amount,
        variants: [{ shopifyId: productData?.variants?.nodes[0]?.id }],
      }

      addVariantsListToCart([{ product, quantity: 1 }])

      getDataLayerProductInfo({
        event: 'add_to_cart',
        items: [
          {
            productTitle: productData?.title,
            price: +productData?.priceRange?.maxVariantPrice?.amount || 0,
            shopifyId: productData?.id,
            brand: 'Heliguy',
            variantTitle: productData?.variants?.nodes[0]?.id,
            quantity: 1,
          },
        ],
      })
    },
    [addVariantsListToCart]
  )

  const onAddToCart = useCallback(
    (data) => {
      const parentId = uuidv4()

      const bonusItems = bonusProducts.map((item) => ({
        product: {
          id: item.id,
          title: item.title,
          price: item.priceRange?.maxVariantPrice?.amount,
          image: item?.featuredImage?.url,
          variants: [{ shopifyId: item.variants?.nodes[0]?.id }],
        },
        additionalData: {
          _parent_id: parentId,
        },
      }))

      if (!Array.isArray(data)) {
        data.additionalData = {
          _id: parentId,
        }
      }

      const array = Array.isArray(data) ? data : [data]
      array.push(...bonusItems)

      const mappedData = array.map((dataObj) => {
        const startDate = new Date(dataObj.startDate)

        const timestamp = parseInt(dataObj.finishDate, 10)
        const finishDate = new Date(timestamp)

        const rentalData = rentalConfig
          ? {
              from: format(startDate, 'dd/MM/yyyy'),
              to: format(finishDate, 'dd/MM/yyyy'),
              units: '1',
              rrp,
              insurance: dataObj.proofOfInsurance,
            }
          : null

        if (rentalData && dataObj?.flyerId) {
          rentalData.flyerId = dataObj.flyerId
        }

        if (rentalData && dataObj?.operatorId) {
          rentalData.operatorId = dataObj.operatorId
        }

        if (rentalData && dataObj?.proofOfTraining) {
          rentalData.training = dataObj.proofOfTraining
        }

        return {
          product: dataObj?.product || cartProductData,
          quantity: +dataObj.weeksCount || quantity,
          isRentalMetafield: !!rentalConfig,
          weeksCount: formValues.weeksCount,
          rentalData,
          additionalData: dataObj?.additionalData,
        }
      })

      getDataLayerProductInfo({
        event: 'add_to_cart',
        items: [
          ...mappedData.map(({ product, weeksCount }) => ({
            productTitle: product?.title,
            price: +product?.price ? +product?.price : 0,
            shopifyId: product?.id.includes('Product') ? product?.id : shopifyId,
            brand: 'Heliguy',
            variantTitle: product?.variants[0]?.shopifyId,
            quantity: weeksCount,
          })),
        ],
      })

      addVariantsListToCart(mappedData)
    },
    [cartProductData, addVariantToCart, rentalConfig, formValues, rrp, quantity, bonusProducts]
  )

  const onSelectVariant = useCallback(
    (variantId) => {
      const selectedVariantObject = variants.find((v) => v?.shopifyId === variantId)
      if (selectedVariantObject) {
        setSelectedVariant(selectedVariantObject)

        const variantShopifyId = selectedVariantObject.shopifyId.replace(
          'gid://shopify/ProductVariant/',
          ''
        )
        changeVariant(variantShopifyId)
      }
    },
    [variants, changeVariant]
  )

  const externalId = useMemo(() => {
    if (!rentalConfig || !rentalConfig.value) return null

    const jsonConfig = JSON.parse(rentalConfig.value)

    if (!jsonConfig) return null

    return jsonConfig.variants[0]?.external_id
  }, [rentalConfig])

  const fetchBooking = useCallback(async (id) => {
    const date = new Date()
    const year = date.getFullYear()
    const month = date.getMonth() + 1

    const formattedMonth = month < 10 ? `0${month}` : month

    const url = `${
      process.env.GATSBY_NETLIFY_API_URL
    }/rental?external_id=${id}&start=${year}-${formattedMonth}-01T00:00&finish=${
      year + 1
    }-${formattedMonth}-01T23:59`

    const response = await axios.get(url)

    const bookings = response.data.blocks

    if (!bookings || !bookings.length) return null

    return bookings
  }, [])

  function isValidDate(d) {
    return d instanceof Date && !isNaN(d)
  }

  const checkIfAvailable = useCallback(
    (startDate, endDate, handle, array) => {
      const start = new Date(startDate)
      const end = new Date(endDate)

      const bookingData = array ? array[handle] : bookingsAccessories.data[handle]

      if (!bookingData || !isValidDate(start) || !isValidDate(end)) return null

      const filteredData = bookingData.filter((item) => {
        const itemDate = new Date(item.date)
        return itemDate >= start && itemDate <= end
      })

      return filteredData.every((item) => item.available_slot_count >= 1)
    },
    [bookingsAccessories]
  )

  useEffect(() => {
    if (!bookingsAccessories.loading && !bookingsAccessories.setup && bookingsAccessories.data) {
      onFormChange(formValues)
      setBookingsAccessories((prevState) => ({ ...prevState, setup: true }))
    }
  }, [bookingsAccessories, formValues])

  useEffect(() => {
    rentalConfig && fetchBooking(externalId).then((bookings) => bookings && setBookings(bookings))
  }, [rentalConfig])

  useEffect(() => {
    if (accessoriesMetafield && accessoriesMetafield?.length) {
      accessoriesClient
        .getAccessoriesByHandles(accessoriesMetafield.split(',') || [])
        .then((accessories) => {
          if (accessories) {
            const promises = []

            rentalConfig &&
              accessories.forEach((accessory) => {
                if (accessory) {
                  const id = accessory.variants?.nodes[0]?.id.replace(
                    'gid://shopify/ProductVariant/',
                    ''
                  )
                  promises.push(
                    fetchBooking(id).then((bookings) => ({
                      [accessory.handle]: bookings,
                    }))
                  )
                }
              })

            Promise.allSettled(promises).then((results) => {
              const booking = results
                .filter((result) => result.status === 'fulfilled')
                .reduce((c, result) => {
                  const [key, value] = Object.entries(result.value)[0]
                  c[key] = value
                  return c
                }, {})

              setBookingsAccessories((prevState) => ({
                ...prevState,
                data: booking,
                loading: false,
              }))
            })

            setAccessories(
              accessories
                .filter((accessory) => accessory)
                .map((accessory) => ({
                  ...accessory,
                  isAvailable: false,
                  inCart: false,
                }))
            )
          }
        })
    }
  }, [accessoriesMetafield])

  // ADD RELATED PRODUCTS
  useEffect(() => {
    if (['Drones'].includes(productType)) {
      relateProductsIdsArray.push('gid://shopify/Product/14891344691586')

      if (tags?.some((tag) => ['Gatsby-Enterprise', 'enterprise'].includes(tag))) {
        relateProductsIdsArray.push('gid://shopify/Product/14891390468482')
      }
    }

    if (shopifyId === 'gid://shopify/Product/14884956897666') {
      relateProductsIdsArray.push('gid://shopify/Product/14922632855938')
    }

    if (shopifyId === 'gid://shopify/Product/14885045338498') {
      relateProductsIdsArray.push('gid://shopify/Product/14922633150850')
    }

    if (relateProductsIdsArray.length === 0) return

    const getRelatedProducts = async (idArray) => {
      setIsRelatedProductsLoading(true)
      const response = await nodeApi.getRelatedProductsByIds(idArray)

      if (!response) {
        setIsRelatedProductsLoading(false)

        return
      }

      setRelatedProducts(response)
      setIsRelatedProductsLoading(false)
    }

    getRelatedProducts(relateProductsIdsArray)
  }, [])

  useEffect(() => {
    if (!bonusItems || bonusItems.length === 0) return

    const getBonusProducts = async (idArray) => {
      setIsBonusProductsLoading(true)

      const response = await nodeApi.getBonusProductsByIds(idArray)

      if (!response) {
        setIsBonusProductsLoading(false)

        return
      }

      setBonusProducts(response.filter(Boolean))
      setIsBonusProductsLoading(false)
    }
    getBonusProducts(bonusItems)
  }, [])

  const onFormChange = useCallback(
    (newValues) => {
      setFormValues({ ...formValues, ...newValues })
      setAccessories((prevState) =>
        prevState.map((item) => ({
          ...item,
          isAvailable: checkIfAvailable(
            newValues.startDate ?? formValues?.startDate,
            newValues.finishDate ?? formValues?.finishDate,
            item.handle
          ),
          inCart: false,
        }))
      )
    },
    [formValues, bookingsAccessories]
  )

  useEffect(() => {
    if (!variants || !variants.length || variant) {
      return
    }

    const firstAvailableVariant = variants?.find((_variant) => _variant?.availableForSale)
    setSelectedVariant(firstAvailableVariant || variants[0])
  }, [variants, variant])

  const onRentalAccessoriesCartAdd = useCallback(
    (handle) => {
      setAccessories((prevState) =>
        prevState.map((item) => (item.handle === handle ? { ...item, inCart: !item.inCart } : item))
      )
    },
    [accessories]
  )

  const onFormSubmit = useCallback(
    async (e, formData, captchaToken) => {
      setLoading(true)
      e.preventDefault()
      const data = extractDataFromFormData(formData)
      const completeData = { ...data, ...formValues }

      const validatedData = await validateValues(completeData)

      if (!validatedData) {
        showToast('Validation error', '')
        setLoading(false)
        return
      }

      const isCaptchaValid = await validateCaptchaToken(captchaToken, showToast, setLoading)

      if (!isCaptchaValid) return

      const formDataPayload = new FormData()
      formDataPayload.set('proofOfInsurance', validatedData.proofOfInsurance)
      // DON'T REMOVE
      // if (validatedData?.proofOfTraining) {
      //   formDataPayload.set('proofOfTraining', validatedData.proofOfTraining);
      // }

      const filesResponse = await axios
        .post(`${process.env.GATSBY_NETLIFY_API_URL}/files_upload`, formDataPayload)
        .catch((err) => {
          showToast('Upload file error', err?.response?.data?.error || 'Failed to upload files')
          return null
        })

      if (!filesResponse) {
        setLoading(false)
        return
      }

      if (filesResponse.data?.error) {
        showToast('Upload file error', filesResponse.data.error)
        setLoading(false)
        return
      }

      const parentId = uuidv4()

      const firstItem = {
        ...validatedData,
        proofOfInsurance: filesResponse.data.proofOfInsurance,
        additionalData: {
          _id: parentId,
        },
      }

      if (validatedData?.proofOfTraining) {
        firstItem.proofOfTraining = filesResponse.data.proofOfTraining
      }

      const itemsToCart = [firstItem]

      const additionalItems = accessories.filter((accessory) => accessory.inCart)

      for (const item of additionalItems) {
        const _objItem = {
          ...validatedData,
          proofOfInsurance: filesResponse.data.proofOfInsurance,
          product: {
            id: item.id,
            title: item.title,
            price: item.priceRange?.maxVariantPrice?.amount,
            image: item?.featuredImage?.url,
            variants: [{ shopifyId: item.variants?.nodes[0]?.id }],
          },
          additionalData: {
            _parent_id: parentId,
          },
        }

        if (validatedData?.proofOfTraining) {
          _objItem.proofOfTraining = filesResponse.data.proofOfTraining
        }

        itemsToCart.push(_objItem)
      }

      onAddToCart(itemsToCart)

      setIsConfirmed(true)

      setLoading(false)
    },
    [formValues, onAddToCart, accessories]
  )

  useEffect(() => {
    const getProductReviews = async () => {
      if (!reviewsPage) return

      try {
        setIsReviewsLoading(true)
        const {
          reviews: receivedReviews,
          totalReviews,
          productRating,
          currentPage,
        } = await getReviewsByProduct(externalIdForReviews, reviewsPage)

        if (!receivedReviews.length) {
          setIsReviewsLoading(false)

          return
        }

        setReviews((prevReviews) =>
          reviewsPage === 1
            ? [...receivedReviews]
            : [
                ...prevReviews,
                ...receivedReviews.filter(
                  ({ id }) => !prevReviews.some((review) => review.id === id)
                ),
              ]
        )
        setRatingInfo((prevState) => ({ ...prevState, totalReviews, productRating }))
        setPageAdditionalValue(currentPage === reviewsPage ? currentPage + 1 : currentPage)
        setIsReviewsLoading(false)
      } catch (e) {
        setIsReviewsLoading(false)
        showToast('Receive reviews error', 'Failed to recive the list of reviews')
      }
    }

    getProductReviews()
  }, [externalIdForReviews, reviewsPage])

  useEffect(() => {
    if (reviews.length === ratingInfo.totalReviews) {
      setIsLoadMore(false)
    } else {
      setIsLoadMore(true)
    }
    if (reviewsPage > 1 && reviewsModalRef.current) {
      reviewsModalRef.current.scrollTo({
        top: reviewsModalRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [reviews.length])

  useEffect(() => {
    setIsReviewsOpen(isReviewNotification)

    const getQuantity = async () => {
      setLoading(true)
      const productInfo = await productApi.getProductQuantity(shopifyId)

      if (!productInfo) {
        setActualTotalCount(totalCount)
        setLoading(false)

        return
      }

      setActualTotalCount(productInfo?.totalInventory)
      setAvailableForSale(productInfo?.availableForSale)

      setLoading(false)
    }

    getQuantity()
  }, [])

  useEffect(() => {
    if (selectedVariant.shopifyId)
      getDataLayerProductInfo({
        event: 'view_item',
        items: [
          {
            productTitle,
            price: selectedVariant?.price,
            shopifyId,
            brand: 'Heliguy',
            variantTitle: selectedVariant?.shopifyId,
            quantity: 1,
          },
        ],
      })
  }, [selectedVariant])

  const ignoreList = []

  const taxNotIncluded = ['gid://shopify/Product/8088746557674'].includes(shopifyId)
  const isFreeDelivery = productType?.toLowerCase() !== 'software' &&
      ![
          "gid://shopify/Product/5797344215200",
          "gid://shopify/Product/5796239540384",
          "gid://shopify/Product/5796252123296"
      ].includes(shopifyId)

  const isBookButton = ["gid://shopify/Product/5797344215200",
    "gid://shopify/Product/5796252123296", "gid://shopify/Product/5796239540384"].includes(shopifyId);
  const deliveryTextHidden = ["gid://shopify/Product/8238420787434",].includes(shopifyId);

  return (
    <ProductHeaderWrapper>
      <EnquireModal
        isOpen={isOpen}
        onClose={handleClose}
        shouldApplyCourse={isFreeCourseProduct}
        freeCourseRef={freeCourseRef}
      />
      <div className="row between-md">
        <div className="col-xs-12 col-md-7 last-xs first-md">
          <PriceWrapper>
            <TypographyAtom type="H1" weight="Bold" copy={productTitle} />
            {sku && sku.length && (
              <SkuText>
                {!ignoreList.includes(shopifyId) && 'SKU:'} {sku}
              </SkuText>
            )}
            {shouldVariantSelectShow && (
              <SelectField
                labelFor="select_variant_product"
                fieldName="variant_product"
                value={selectedVariant?.shopifyId}
                onChange={(e) => onSelectVariant(e.target.value)}
                options={(variants || []).map((variant) => ({
                  label: variant?.title,
                  value: variant?.shopifyId,
                }))}
                placeholderText="Select variant"
                fieldId="select_variant_product"
                LabelText="Variant"
              />
            )}
            <PriceTag
              hidePrice={hidePrice}
              enquireWithPrice={enquireWithPrice}
              rentalConfig={rentalConfig}
              selectedVariant={selectedVariant}
              isFreeCourseProduct={isFreeCourseProduct}
              taxIncluded={!taxNotIncluded}
              isFreeDelivery={isFreeDelivery}
              deliveryTextHidden={deliveryTextHidden}
            />
            {isCtaTextDisplayed && (
              <TypographyAtom
                className="font-size-18"
                type="P"
                weight="Regular"
                color="Orange"
                copy={ctaTextBox}
              />
            )}
            <PriceWrapperFooter
              addSpace={rentalConfig || hidePrice || enquireWithPrice || showNotify}
            >
              <PriceFooterInner>
                {!rentalConfig && (!hidePrice || hidePrice && actualTotalCount > 0) && !enquireWithPrice && (
                  <StockLabel value={actualTotalCount} isLoading={isLoading} />
                )}
                {rrp && (
                  <PurchasePriceWrapper className="purchasePrice">
                    <TypographyAtom type="P" weight="Regular" copy={`Purchase Price: ${rrp}`} />
                  </PurchasePriceWrapper>
                )}
                <AddReviewButton
                  reviews={ratingInfo.totalReviews || 0}
                  rating={ratingInfo.productRating || 0}
                  isLoading={isReviewsLoading}
                  onClick={handleOpenRevies}
                  className="add-review-button"
                />
              </PriceFooterInner>
              {!rentalConfig && !hidePrice && !enquireWithPrice && !showNotify ? (
                <AddToCartWrapper>
                  <QuantityField
                    value={quantity}
                    onChange={handleChangeQuantity}
                    max={isInventoryTracks ? actualTotalCount : undefined}
                    aria-label="set quantity"
                  />
                  <ButtonsWrapper>
                    {!!actualTotalCount && !isBookButton && (
                      <ButtonAtom
                        onClick={onAddToCart}
                        buttonType="Primary Button"
                        buttonCopy="Add to cart"
                        isLoading={isBonusProductsLoading}
                      />
                    )}
                    {isBookButton && (
                        <ButtonAtom
                            onClick={()=>navigate('https://support.heliguy.com/open.php')}
                            buttonType="Primary Button"
                            buttonCopy="Book a service"
                            isLoading={isBonusProductsLoading}
                        />
                    )}
                    <ButtonAtom
                      onClick={handleOpen}
                      buttonType="Secondary Button Without Arrow"
                      buttonCopy={returnEnquireButtonText()}
                    />
                  </ButtonsWrapper>
                </AddToCartWrapper>
              ) : (
                <ButtonsWrapper>
                  <ButtonAtom
                    onClick={handleOpen}
                    buttonType="Secondary Button Without Arrow"
                    buttonCopy={returnEnquireButtonText()}
                  />
                </ButtonsWrapper>
              )}

              {/* {hidePrice && ( */}
              {/* <EnquireNowWrapper></EnquireNowWrapper> */}
              {/* )} */}
              {/* {!hidePrice && showNotify && (
                <DisabledButtonWrapper>
                  <ButtonAtom buttonType="Primary Button" buttonCopy="Sold out" />
                </DisabledButtonWrapper>
              )} */}
            </PriceWrapperFooter>
            {rentalProductSlug && (
              <LinkAtom
                linkType="Rental Button"
                linkDestination={`${ROUTES.PRODUCTS}/${rentalProductSlug}`}
                linkText="Go to the rental product"
              />
            )}
            {!rentalConfig && upgradeButtonText && (
              <LinkAtom
                linkType="Upgrade Button"
                linkDestination={`/${getUpgradeButtonLink(upgradeButtonLink)}`}
                linkText={upgradeButtonText}
              />
            )}
          </PriceWrapper>
          {isRelatedProductsLoading ? (
            <Loader />
          ) : (
            relatedProducts.length > 0 &&
            actualTotalCount > 0 && (
              <div className="margin-40">
                <RelatedProducts
                  relatedProducts={relatedProducts}
                  onAction={onAddRelatedProducts}
                  onEnquireNowOpen={setIsOpen}
                  isLoading={isRelatedProductsLoading}
                />
              </div>
            )
          )}
          {showNotify && !hidePrice && !enquireWithPrice && (
            <NotifyProduct selectedVariant={selectedVariant} />
          )}
          {rentalConfig && !showNotify && (
            <>
              <div className="margin-40">
                <RentalForm
                  bookings={bookings}
                  onChange={onFormChange}
                  formValues={formValues}
                  price={selectedVariant?.price}
                  deliveryPrice={deliveryPrice}
                />
              </div>
              {accessories && accessories.length > 0 && (
                <div className="margin-40">
                  <RentalProductAccessories
                    accessories={accessories}
                    onAction={onRentalAccessoriesCartAdd}
                    loadingBooking={bookingsAccessories.loading}
                  />
                </div>
              )}
            </>
          )}
          {rentalConfig && (
            <div className="margin-40">
              <div className="margin-40">
                <TypographyAtom
                  type="H4"
                  copy="Please book your rental online at least 48 hours in advance. If you require it sooner, kindly call us"
                  weight="Bold"
                />
              </div>
              <div className={`${upgradeButtonText ? 'margin-8' : 'margin-none'}`}>
                <ButtonAtom
                  onClick={handleBookNowClick}
                  buttonType="Primary Button"
                  buttonCopy="Book now"
                />
              </div>
              {upgradeButtonText && (
                <LinkAtom
                  linkType="Upgrade Button"
                  linkDestination={`/${getUpgradeButtonLink(upgradeButtonLink)}`}
                  linkText={upgradeButtonText}
                />
              )}
            </div>
          )}

          <DetailsWrapper>
            {productDescriptionContentful === undefined ? (
              <div dangerouslySetInnerHTML={{ __html: productDescriptionShopify }} />
            ) : (
              <TypographyAtom
                type="BlockText"
                copyBlock={productDescriptionContentful}
                weight="BlockText"
              />
            )}
          </DetailsWrapper>
        </div>
        <div className="col-xs-12 col-md-5 first-xs last-md">
          <ImageWrapper>
            {isSale && !hidePrice && !enquireWithPrice && <SaleLabel top={0} right={0} />}
            <StickyWrapper>
              <ProductGallery
                carouselImages={featureImage ? [...productImages, featureImage] : productImages}
              />
            </StickyWrapper>
          </ImageWrapper>
        </div>
      </div>
      <RentalPoductModal
        open={isRentalModalOpen}
        onClose={handleCloseRentalModal}
        isConfirmed={isConfirmed}
      >
        <FileUploadForm
          onSubmit={onFormSubmit}
          isDrone={isDrone}
          metafields={rentalMetaFields}
          loading={isLoading}
          setIsLoading={setLoading}
          isConfirmed={isConfirmed}
          onFinish={handleFinishUploading}
          continueBrowsingLink={familyCollection}
          validateInput={validateFieldOnChange}
          fieldErrors={errors}
        />
      </RentalPoductModal>
      <ReviewsModal
        currentRef={reviewsModalRef}
        open={isReviewsOpen}
        reviews={reviews}
        variants={variants}
        productTitle={productTitle}
        productInfo={
          <PriceTag
            hidePrice={hidePrice}
            enquireWithPrice={enquireWithPrice}
            rentalConfig={rentalConfig}
            selectedVariant={selectedVariant}
            isFreeCourseProduct={isFreeCourseProduct}
          />
        }
        productId={externalIdForReviews}
        productRating={ratingInfo.productRating}
        totalReviews={ratingInfo.totalReviews}
        totalCount={actualTotalCount}
        isLoadMore={isLoadMore}
        isReviewsLoading={isReviewsLoading}
        isRefetching={isReviewsLoading && reviewsPage === 1}
        retingFromNotification={retingFromNotification}
        onLoadMore={handleSetReviewsPage}
        onClose={handleCloseReviews}
      />
    </ProductHeaderWrapper>
  )
}

ProductHeader.propTypes = {
  productTitle: PropTypes.string.isRequired,
  productId: PropTypes.string.isRequired,
  altTag: PropTypes.string.isRequired,
  imgSrc: PropTypes.string.isRequired,
  productDescriptionShopify: PropTypes.string,
  productDescriptionContentful: PropTypes.string,
  totalCount: PropTypes.number,
  variants: PropTypes.array,
  tags: PropTypes.array,
  productImages: PropTypes.array,
  rentalMetaFields: PropTypes.array,
  isInventoryTracks: PropTypes.bool,
}
